import { useState, useEffect, useMemo, useContext, useRef } from "react";
import {
  TextField,
  MenuItem,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  ListSubheader,
  InputAdornment,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  documentId,
  where,
  or,
  and,
  getCountFromServer,
  doc,
  getDoc
} from "firebase/firestore";
import { Search, Info as InfoIcon } from "@mui/icons-material";
import { GridClearIcon } from "@mui/x-data-grid";
import { Context } from "context/Wrapper";
import { db } from "config/config";
import tableConfig from "config/advancedTableColumnsControl.json";

export const fetchData = async ({
  lastVisible,
  collectionName,
  searchText = "",
  conditions = [],
  label,
  batchSize = 10,
}) => {
  let q;

  if (conditions?.length === 0) return { data: [], lastVisible: null };

  if (lastVisible)
    q = query(
      collection(db, collectionName),
      orderBy(documentId(), "desc"),
      startAfter(lastVisible),
      limit(batchSize),
      ...conditions
    );
  else
    q = query(
      collection(db, collectionName),
      orderBy(documentId(), "desc"),
      limit(batchSize),
      ...conditions
    );

  let count = 0;
  const searchLimit = tableConfig?.searchMinLength ?? 2;

  if (`${searchText}`.trim()?.length > searchLimit) {
    q = query(
      collection(db, collectionName),
      orderBy(label, "desc"),
      orderBy(documentId(), "desc"),
      ...conditions
    );
    count = 0;
  } else {
    count = (
      await getCountFromServer(
        query(collection(db, collectionName), ...conditions)
      )
    )?.data()?.count;
  }

  const snapshot = await getDocs(q);
  const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  const lastVisibleDoc =
    searchText?.length > searchLimit ? null : snapshot.docs[snapshot.docs.length - 1];

  return { data, lastVisible: lastVisibleDoc, count };
};

const InfiniteScrollSelect = ({
  collectionName = "",
  label = "name",
  inputLabel,
  onChange,
  value,
  name,
  data,
  disableClear = false,
}) => {
  const selectElem = useRef(null);
  const context = useContext(Context);

  const { groupId, isPro, userId } = context?.state?.userProfile || {};
  const { showAllDataFlag } = context?.state || {};

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [count, setCount] = useState(0);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  const searchDebounceRef = useRef(null);
  const nameCacheRef = useRef(new Map());

  const searchLimit = tableConfig?.searchMinLength ?? 2;

  useEffect(() => {
    if (Array.isArray(data)) setOptions(data);
  }, [data]);

  useEffect(() => {
    setAllDataLoaded(false);
  }, [showAllDataFlag]);

  const CONTACT_LABELS = [
    "Owner",
    "Tenant",
    "Client",
    "Filing representative",
    "Contractor",
    "Inspector Name"
  ];
  const isContactDropdown = CONTACT_LABELS.includes(inputLabel);

  const baseFilter = useMemo(() => {
    if (!userId) return null;
    if (isPro && groupId && (isContactDropdown || showAllDataFlag)) {
      return or(
        where("groupId", "==", groupId),
        where("userId", "==", userId)
      );
    }
    return where("userId", "==", userId);
  }, [userId, groupId, isPro, isContactDropdown, showAllDataFlag]);

  const additionalCondition = useMemo(() => {
    if (inputLabel === "Owner") return where("owner", "==", true);
    if (inputLabel === "Tenant") return where("tenant", "==", true);
    if (inputLabel === "Client") return where("client", "==", true);
    if (inputLabel === "Filing representative") return where("filingRep", "==", true);
    if (inputLabel === "Contractor") return where("contractor", "==", true);
    if (inputLabel === "Inspector Name") {
      return or(where("architect", "==", true), where("engineer", "==", true));
    }
    return null;
  }, [inputLabel]);

  const buildConditions = () => {
    const conditions = [];
    if (baseFilter) {
      if (additionalCondition) {
        conditions.push(and(baseFilter, additionalCondition));
      } else {
        conditions.push(baseFilter);
      }
    }
    return conditions;
  };

  useEffect(() => {
    if (allDataLoaded) return;
    setLastVisible(null);
    if (!open) return; 
    void loadOptions({ reset: options.length === 0 }); // lazy initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllDataFlag, searchText, userId, open]);

  useEffect(() => {
    if (!value || !collectionName) return;
    if (options.some(o => o.id === value)) return;

    let mounted = true;
    (async () => {
      try {
        const dRef = doc(db, collectionName, value);
        const dSnap = await getDoc(dRef);
        if (!mounted || !dSnap.exists()) return;
        setOptions(prev => (prev.some(p => p.id === value) ? prev : [...prev, { id: dSnap.id, ...dSnap.data() }]));
      } catch (_) { /* noop */ }
    })();
    return () => { mounted = false; };
  }, [value, collectionName, options]);

  const enrichWithNames = async (items) => {
    // Fetch owner/tenant full names with caching; keeps tooltips without heavy re-reads
    const toFetch = [];
    items.forEach((it) => {
      ["owner", "tenant"].forEach((k) => {
        const id = it?.[k];
        if (typeof id === "string" && id.trim() && !nameCacheRef.current.has(id)) {
          toFetch.push(id);
        }
      });
    });
    // Batch in chunks of 10 (Firestore 'in' query limit)
    const chunk = 10;
    for (let i = 0; i < toFetch.length; i += chunk) {
      const group = toFetch.slice(i, i + chunk);
      try {
        const qs = await getDocs(
          query(collection(db, "contacts"), where(documentId(), "in", group))
        );
        qs.docs.forEach((d) => {
          const fullName = d.data()?.fullName || "";
          nameCacheRef.current.set(d.id, fullName);
        });
      } catch {
        // Fallback: if batched fetch fails for any reason, ignore (tooltips optional)
        for (const id of group) {
          if (!nameCacheRef.current.has(id)) nameCacheRef.current.set(id, "");
        }
      }
    }
    return items.map((it) => ({
      ...it,
      ownerFullName: typeof it.owner === "string" ? (nameCacheRef.current.get(it.owner) || "") : "",
      tenantFullName: typeof it.tenant === "string" ? (nameCacheRef.current.get(it.tenant) || "") : "",
    }));
  };

  const loadOptions = async ({ reset = false } = {}) => {
    if (!collectionName) return;
    const conditions = buildConditions();
    if (conditions.length === 0) {
      // Keep behavior: no user context, no data
      return;
    }

    setLoading(true);
    try {
      const {
        data: page,
        lastVisible: newLast,
        count: totalCount
      } = await fetchData({
        lastVisible: reset ? null : lastVisible,
        collectionName,
        searchText,
        label,
        batchSize: 10,
        conditions,
      });

      const pageWithNames = await enrichWithNames(page);

      setOptions((prev) => {
        const base = reset ? [] : (Array.isArray(prev) ? prev : []);
        const merged = [...base];
        for (const item of pageWithNames) {
          if (!merged.some(x => x.id === item.id)) merged.push(item);
        }
        return merged;
      });

      setLastVisible(newLast || null);

      // When searching with text (>= limit), we don't infinite-scroll,
      // so we mark as loaded to avoid extra work.
      const searching = `${searchText}`.trim().length > searchLimit;
      setAllDataLoaded(searching || !newLast);
      if (!searching) setCount(totalCount || 0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // Lazy-load if empty
    if (options.length === 0 && !loading) {
      void loadOptions({ reset: true });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearchText("");
  };

  const handleScroll = (event) => {
    if (loading || allDataLoaded) return;
    if (options?.length >= count && count > 0) return;
    const bottom =
      event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
    if (bottom && !loading) {
      void loadOptions();
    }
  };

  const handleSearchInput = (e) => {
    const val = e.target.value;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    // Local debounce only; no global state thrash
    searchDebounceRef.current = setTimeout(() => {
      setSearchText(val);
      // When searching, we pull a fresh page; mark not fully loaded so first fetch can run
      setAllDataLoaded(false);
      setLastVisible(null);
    }, 500);
  };

  const searchCheck = `${searchText}`.trim().length > searchLimit;

  // Pure memo filtering — no state, no timers
  const optionsCollection = useMemo(() => {
    if (!searchCheck) return options;
    const term = `${searchText}`.toLowerCase();
    return (options || []).filter(
      (item) => typeof item?.[label] === "string" && item[label].toLowerCase().includes(term)
    );
  }, [options, label, searchText, searchCheck]);

  const infoTooltipContent = (
    <div>
      Make sure you select the appropriate contact type (under the Contacts page) so that it appears here.
    </div>
  );

  const infoTooltipContentForInspectionPage = (
    <div>
      Make sure you select the appropriate contact type (under the Contacts page) so that it appears here. An inspector can be an Engineer or an Architect.
    </div>
  );

  return (
    <FormControl fullWidth>
      <InputLabel id={`infinite-select-label-${name}`}>{inputLabel}</InputLabel>

      <Select
        inputRef={selectElem}
        name={name}
        labelId={`infinite-select-label-${name}`}
        label={inputLabel}
        id="infinite-select"
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={(...rest) =>
          onChange(
            ...rest,
            optionsCollection?.find(
              (item) => item?.id === rest?.[1]?.props?.value
            )
          )
        }
        value={value || ""}
        MenuProps={{
          PaperProps: {
            onScroll: handleScroll,
            sx: { maxHeight: "300px" },
          },
          autoFocus: false,
        }}
        endAdornment={
          <>
            {(inputLabel === "Inspector Name" ||
              inputLabel === "Owner" ||
              inputLabel === "Tenant" ||
              inputLabel === "Client" ||
              inputLabel === "Filing representative" ||
              inputLabel === "Contractor") && (
              <InputAdornment position="end">
                <Tooltip
                  title={inputLabel === "Inspector Name" ? infoTooltipContentForInspectionPage : infoTooltipContent}
                  arrow
                >
                  <IconButton
                    sx={{ right: value ? 0 : 10 }}
                    tabIndex={-1}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            )}

            {value && !disableClear && (
              <InputAdornment sx={{ marginRight: "10px" }} position="end">
                <IconButton
                  onClick={() => {
                    // Keep existing behavior for clearing
                    if (selectElem.current?.node) {
                      selectElem.current.node.value = "";
                      onChange({ target: selectElem.current.node });
                    } else {
                      onChange({ target: { value: "" } });
                    }
                  }}
                  tabIndex={-1}
                >
                  <GridClearIcon />
                </IconButton>
              </InputAdornment>
            )}
          </>
        }
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            onChange={handleSearchInput}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                // Prevent autoselecting item while typing (default Select behaviour)
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>

        {(optionsCollection || []).map((option) => {
          const labelValue = option?.[label] || "";
          const extraBusiness = option?.businessName ? ` (${option?.businessName})` : "";
          const ownerFullName = option?.ownerFullName || "";
          const tenantFullName = option?.tenantFullName || "";

          const tooltipContent = (
            <>
              {ownerFullName && <div>Owner: {ownerFullName}</div>}
              {tenantFullName && <div>Tenant: {tenantFullName}</div>}
            </>
          );

          const displayValue = `${labelValue}${extraBusiness}`;

          return (
            <MenuItem key={option.id} value={option.id}>
              {(ownerFullName || tenantFullName) ? (
                <Tooltip title={tooltipContent} arrow>
                  <span
                    style={{ display: "inline-block", width: "100%" }}
                    dangerouslySetInnerHTML={{ __html: displayValue }}
                  />
                </Tooltip>
              ) : (
                <span
                  style={{ display: "inline-block", width: "100%" }}
                  dangerouslySetInnerHTML={{ __html: displayValue }}
                />
              )}
            </MenuItem>
          );
        })}

        {loading && (
          <MenuItem disabled>
            <CircularProgress size={24} />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default InfiniteScrollSelect;
